import { Box, Button, Divider } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress/CircularProgress'
import CssBaseline from '@mui/material/CssBaseline/CssBaseline'
import ThemeProvider from '@mui/material/styles/ThemeProvider'
import activateVersionIcon from 'assets/Arrowrew.png'
import moment from 'moment'
import { ReactNode, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { COLORS } from 'styles/colors'
import { CardStandart } from 'ui/CardStandart'
import { TypographyHeader } from 'ui/typography/TypographyHeader'
import { TypographyPrimary } from 'ui/typography/TypographyPrimary'
import { TypographyPrimary14Bold } from 'ui/typography/TypographyPrimary14Bold'
import { TypographySecondary } from 'ui/typography/TypographySecondary'
import { TypographySecondary14 } from 'ui/typography/TypographySecondary14'
import {
	IRationComponents,
	IRationTableComponents,
} from '../../models/IRationSystem'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import {
	fetchComponentsById,
	fetchRationById,
} from '../../store/rationSystemSlice'
import { fontTheme } from '../../styles/font'
import CustomTooltip from '../shared/CustomTooltip'
import { roundingNumber } from '../shared/processes'
import RationActivateVersionDrawer from './RationActivateVersionDrawer'

const tableColumns = {
	display: 'grid',
	gridTemplateColumns: 'minmax(80px, 1.3fr) 1fr 1fr 1fr',
	alignItems: 'center',
	columnGap: '4px',
}

const formatDate = (value?: number | string | null) => {
	if (!value) return '—'
	const timestamp =
		typeof value === 'number' && value < 1e12 ? value * 1000 : value
	const date = moment(timestamp)
	return date.isValid() ? date.format('DD.MM.YYYY') : '—'
}

const quoteTitle = (title?: string) => (title ? `“${title}”` : '')

const InfoLine = ({ label, value }: { label: string; value?: string }) => (
	<Box
		sx={{
			marginTop: '14px',
			display: 'flex',
			flexWrap: 'wrap',
			gap: '8px',
		}}
	>
		<TypographySecondary14>{label}</TypographySecondary14>
		<TypographyPrimary>{value || '—'}</TypographyPrimary>
	</Box>
)

const AmountCell = ({
	value,
	unit,
	bold,
}: {
	value: number | string
	unit: string
	bold?: boolean
}) => {
	const Text = bold ? TypographyPrimary14Bold : TypographyPrimary
	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
			}}
		>
			<Text>{value}</Text>
			<TypographySecondary sx={{ marginLeft: '4px' }}>
				{unit}
			</TypographySecondary>
		</Box>
	)
}

const CompositionBlock = ({
	humidity,
	components,
	catalog,
}: {
	humidity?: number
	components?: IRationComponents[]
	catalog: IRationTableComponents[]
}) => {
	const rows = (components ?? [])
		.map(component => {
			const catalogItem = catalog.find(
				item => item.id === component.component_id,
			)
			if (!catalogItem) return null
			const weight =
				(+component.dry_per_head * 100) / catalogItem.dry_matter_percent
			return {
				id: component.component_id,
				title: catalogItem.title,
				dryPercent: catalogItem.dry_matter_percent,
				dryPerHead: +component.dry_per_head,
				weight,
			}
		})
		.filter(Boolean) as {
		id: number
		title: string
		dryPercent: number
		dryPerHead: number
		weight: number
	}[]
	const dryTotal = rows.reduce((sum, row) => sum + row.dryPerHead, 0)
	const weightTotal = rows.reduce((sum, row) => sum + row.weight, 0)
	const dryPercent = 100 - Number(humidity || 0)
	const waterWeight = dryPercent
		? (100 * dryTotal - dryPercent * weightTotal) / dryPercent
		: 0

	return (
		<Box
			sx={{
				marginTop: '24px',
				flex: 1,
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			<TypographyPrimary14Bold sx={{ fontSize: '16px' }}>
				Состав рациона
			</TypographyPrimary14Bold>
			<Box sx={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
				<TypographySecondary14>Влажность рациона:</TypographySecondary14>
				<TypographyPrimary>
					{roundingNumber(humidity || 0, 2)}{' '}
					<span style={{ color: COLORS.secondaryFont }}>%</span>
				</TypographyPrimary>
			</Box>
			<Box sx={{ ...tableColumns, marginTop: '16px' }}>
				<TypographySecondary>Компоненты</TypographySecondary>
				<TypographySecondary sx={{ textAlign: 'center' }}>
					СВ, %
				</TypographySecondary>
				<TypographySecondary sx={{ textAlign: 'center' }}>
					СВ, кг/голову
				</TypographySecondary>
				<TypographySecondary sx={{ textAlign: 'center' }}>
					Вес, кг/голову
				</TypographySecondary>
			</Box>
			<Divider sx={{ marginTop: '8px' }} />
			<Box sx={{ ...tableColumns, minHeight: '52px', marginTop: '8px' }}>
				<TypographyPrimary>Вода</TypographyPrimary>
				<TypographySecondary sx={{ textAlign: 'center' }}>
					-
				</TypographySecondary>
				<TypographySecondary sx={{ textAlign: 'center' }}>
					-
				</TypographySecondary>
				<AmountCell value={roundingNumber(waterWeight, 2)} unit='кг/гол' />
			</Box>
			{rows.map(row => (
				<Box key={row.id} sx={{ ...tableColumns, minHeight: '52px' }}>
					<TypographyPrimary>{row.title}</TypographyPrimary>
					<AmountCell value={roundingNumber(row.dryPercent, 2)} unit='%' />
					<AmountCell value={roundingNumber(row.dryPerHead, 2)} unit='кг/гол' />
					<AmountCell value={roundingNumber(row.weight, 2)} unit='кг/гол' />
				</Box>
			))}
			<Box sx={{ flex: 1, minHeight: '8px' }} />
			<Divider />
			<Box sx={{ ...tableColumns, marginTop: '16px' }}>
				<TypographyPrimary14Bold sx={{ textAlign: 'right' }}>
					Итого в рационе:
				</TypographyPrimary14Bold>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					<TypographyPrimary14Bold>
						{roundingNumber(dryPercent, 2)}
					</TypographyPrimary14Bold>
					<TypographySecondary sx={{ marginLeft: '4px', marginRight: '4px' }}>
						%
					</TypographySecondary>
					<CustomTooltip
						width='16px'
						height='16px'
						info={true}
						placement='top'
						title='СВ,% рациона расчитывается исходя из долей компонентов в рационе.'
					/>
				</Box>
				<AmountCell value={roundingNumber(dryTotal, 2)} unit='кг/гол' bold />
				<AmountCell
					value={roundingNumber(weightTotal + waterWeight, 2)}
					unit='кг/гол'
					bold
				/>
			</Box>
		</Box>
	)
}

const RationVersionComparePage = () => {
	const { rationid, versionid } = useParams()
	const dispatch = useAppDispatch()
	const navigate = useNavigate()
	const [activateOpen, setActivateOpen] = useState(false)
	const { rationInfo, rationCompById, loading } = useAppSelector(
		state => state.rationSystemReducer,
	)
	const { userData } = useAppSelector(state => state.userReducer)
	const hasFoodIntegration = Boolean(userData?.company?.food_integration)

	useEffect(() => {
		if (!rationid) return
		dispatch(fetchComponentsById(rationid))
		dispatch(fetchRationById(rationid))
	}, [dispatch, rationid])

	if (loading || !rationInfo || !rationCompById) {
		return (
			<Box
				sx={{
					marginTop: '25%',
					display: 'flex',
					justifyContent: 'center',
				}}
			>
				<CircularProgress
					sx={{
						'&.MuiCircularProgress-colorPrimary': { color: COLORS.main },
					}}
				/>
			</Box>
		)
	}

	const versions = rationInfo.versions ?? []
	const selectedVersion = versions.find(
		version => String(version.id) === String(versionid),
	)
	const firstVersion = [...versions].sort(
		(left, right) => (left.version_number ?? 0) - (right.version_number ?? 0),
	)[0]
	const nextVersion = versions.find(
		version =>
			version.version_number === (selectedVersion?.version_number ?? 0) + 1,
	)
	const creatorName = firstVersion?.created_by_name || ''
	const createdAt = formatDate(rationInfo.created_dt || rationInfo.created_at)
	const currentTitle = rationInfo.active_version?.title || rationInfo.title
	const selectedTitle = selectedVersion?.title || rationInfo.title

	const versionDate = formatDate(
		selectedVersion?.archived_at || selectedVersion?.created_at,
	)

	return (
		<ThemeProvider theme={fontTheme}>
			<CssBaseline />
			<Box sx={{ pl: '16px', pr: '40px', pb: '24px' }}>
				<CardStandart
					sx={{
						display: 'grid',
						gridTemplateColumns: '1fr 1fr',
						minHeight: 'calc(100vh - 124px)',
						'&:hover': { boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.08)' },
					}}
				>
					<VersionHalf
						headerSlot={
							<>
								<TypographyHeader sx={{ fontSize: '18px' }}>
									Сравнение рационов
								</TypographyHeader>
								{!hasFoodIntegration && selectedVersion && (
									<ActivateButton onClick={() => setActivateOpen(true)} />
								)}
							</>
						}
						heading={`Версия рациона ${quoteTitle(selectedTitle)}`}
						title={selectedTitle}
						createdAt={createdAt}
						creatorName={creatorName}
						editedAt={formatDate(selectedVersion?.archived_at)}
						editorName={nextVersion?.created_by_name || ''}
						humidity={selectedVersion?.food_humidity}
						components={selectedVersion?.components}
						catalog={rationCompById}
					/>
					<VersionHalf
						background='#FAFAFA'
						heading={`Активный рацион ${quoteTitle(currentTitle)}`}
						title={currentTitle}
						createdAt={createdAt}
						creatorName={creatorName}
						humidity={rationInfo.food_humidity}
						components={rationInfo.components}
						catalog={rationCompById}
					/>
				</CardStandart>
				{selectedVersion && rationInfo.id && (
					<RationActivateVersionDrawer
						open={activateOpen}
						onClose={() => setActivateOpen(false)}
						rationId={rationInfo.id}
						versionId={selectedVersion.id}
						versionDate={versionDate}
						activeVersionId={rationInfo.active_version?.id}
						onSuccess={() => navigate(`/feed/ration/${rationInfo.id}`)}
					/>
				)}
			</Box>
		</ThemeProvider>
	)
}

const ActivateButton = ({ onClick }: { onClick: () => void }) => (
	<Button
		variant='outlined'
		onClick={onClick}
		disableRipple
		disableElevation
		startIcon={
			<Box
				component='span'
				sx={{
					width: 18,
					height: 18,
					display: 'block',
					backgroundColor: COLORS.main,
					mask: `url(${activateVersionIcon}) center / contain no-repeat`,
					WebkitMask: `url(${activateVersionIcon}) center / contain no-repeat`,
				}}
			/>
		}
		sx={{
			height: '40px',
			borderRadius: '12px',
			textTransform: 'none',
			fontWeight: 600,
			fontSize: '14px',
			color: COLORS.main,
			borderColor: COLORS.main,
			backgroundColor: 'white',
			boxShadow: 'none',
			paddingLeft: '14px',
			paddingRight: '16px',
			whiteSpace: 'nowrap',
			'&:hover, &:focus, &:active': {
				borderColor: COLORS.main,
				backgroundColor: 'white',
				boxShadow: 'none',
			},
		}}
	>
		Активировать эту версию
	</Button>
)

const VersionHalf = ({
	headerSlot,
	background = 'white',
	heading,
	title,
	createdAt,
	creatorName,
	editedAt,
	editorName,
	humidity,
	components,
	catalog,
}: {
	headerSlot?: ReactNode
	background?: string
	heading: string
	title?: string
	createdAt: string
	creatorName?: string
	editedAt?: string
	editorName?: string
	humidity?: number
	components?: IRationComponents[]
	catalog: IRationTableComponents[]
}) => (
	<Box
		sx={{
			padding: '24px',
			backgroundColor: background,
			display: 'flex',
			flexDirection: 'column',
			minWidth: 0,
		}}
	>
		<Box
			sx={{
				height: '36px',
				display: 'flex',
				alignItems: 'center',
				gap: '24px',
			}}
		>
			{headerSlot}
		</Box>
		<TypographyPrimary14Bold sx={{ marginTop: '24px', fontSize: '16px' }}>
			{heading}
		</TypographyPrimary14Bold>
		<Box sx={{ marginTop: '4px', minHeight: '155px' }}>
			<InfoLine label='Название рациона:' value={title} />
			<InfoLine label='Дата создания:' value={createdAt} />
			<InfoLine label='Создал:' value={creatorName} />
			{editedAt !== undefined && (
				<InfoLine label='Дата редактирования:' value={editedAt} />
			)}
			{editorName !== undefined && (
				<InfoLine label='Редактировал:' value={editorName} />
			)}
		</Box>
		<CompositionBlock
			humidity={humidity}
			components={components}
			catalog={catalog}
		/>
	</Box>
)

export default RationVersionComparePage
